import React, { createContext, useEffect, useState } from 'react'

import { useFormContext } from '@guiker/react-hook-form'
import { UseMutateFunction } from '@guiker/react-query'
import { generateUseContext } from '@guiker/react-utils'
import { flattenObject, isObjectLike, sleep } from '@guiker/shared-framework'

import { FormSubmitHandlerGenerator, MutationOptions, OnSubmit } from '../../types'
import { useFormEvent } from '../../utils'

type Context = {
  formName: string
  readOnly: boolean
  isSubmitting: boolean
  generateFormSubmitHandler: FormSubmitHandlerGenerator
}

const ApiFormContext = createContext<Context>(undefined)

type Props<TFormValues, TResult> = {
  formName: string
  onSubmit?: OnSubmit<TResult, TFormValues>
  skipIfIsNotDirty?: boolean
  apiOptions?: MutationOptions<TResult, TFormValues> & {
    skipMutation?: boolean
    onSubmitWithoutChange?: () => void
  }
  readOnly: boolean
} & React.PropsWithChildren

const ApiFormContextProvider = <TResult, TFormValues extends Record<string, any> = Record<string, any>>({
  formName,
  onSubmit,
  skipIfIsNotDirty,
  apiOptions,
  readOnly: readOnlyProp,
  children,
}: Props<TFormValues, TResult>) => {
  const { handleSubmit, formState, getValues, setValue, beforeValidation } = useFormContext()
  const { sendFormEvent, handleFormError } = useFormEvent(formName)
  const { isDirty } = formState

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [readOnly, setReadOnly] = useState(readOnlyProp)

  const onFormSubmit = async (payload: TFormValues, mutate: UseMutateFunction<TResult, unknown, TFormValues>) => {
    if (!isDirty && !!apiOptions.onSubmitWithoutChange) {
      apiOptions.onSubmitWithoutChange()
      sendFormEvent({ event: 'formSubmitSuccess', formName })
    } else if (apiOptions.skipMutation) {
      if (!!onSubmit) {
        try {
          await onSubmit(payload)
          sendFormEvent({ event: 'formSubmitSuccess', formName })
        } catch (error) {
          handleFormError(error, { payload })
        }
      } else {
        handleFormError(new Error('Unable to call undefined onSubmit method'))
      }
    } else {
      mutate(payload, {
        onSuccess: () => sendFormEvent({ event: 'formSubmitSuccess', formName }),
        onError: (error: Error) => {
          handleFormError(error, { payload })
        },
      })
    }
  }

  const clearStates = () => {
    setIsSubmitting(false)
  }

  const generateFormSubmitHandler = (mutate: UseMutateFunction<TResult, unknown, TFormValues>) => {
    const actualSubmit = handleSubmit((data: TFormValues) => {
      sendFormEvent({ event: 'formSubmitAttempt', formName })

      return onFormSubmit(data, mutate)
    })

    return async (e) => {
      if (e && e.preventDefault) {
        e.preventDefault()
        e.persist()
        e.stopPropagation()
      }

      setIsSubmitting(true)
      if (beforeValidation && (!skipIfIsNotDirty || isDirty)) {
        const formData = getValues() as TFormValues

        return beforeValidation({ formData })
          .then((results) => {
            if (results && isObjectLike(results)) {
              Object.entries(flattenObject(results)).forEach(([name, value]) => {
                setValue(name, value)
              })
            } else if (!results) {
              return sleep(1000)
            }
          })
          .then(() => actualSubmit(e))
          .finally(() => clearStates())
      } else {
        return actualSubmit(e).finally(() => clearStates())
      }
    }
  }

  useEffect(() => {
    setReadOnly(readOnlyProp)
  }, [readOnlyProp])

  return (
    <ApiFormContext.Provider
      value={{
        formName,
        readOnly,
        isSubmitting,
        generateFormSubmitHandler,
      }}
    >
      {children}
    </ApiFormContext.Provider>
  )
}

const useApiFormContext = generateUseContext(ApiFormContext)

export { ApiFormContextProvider, useApiFormContext }
